import { useState } from 'react';
import { Alert, Skeleton, Stack } from '@mantine/core';
import { IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import { TV_BRAND_KIT_COPY, type FactoryAssetPlatform } from '@/lib/constants/appFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { useFactoryAssetSets } from '../useAppFactory';
import { AppCurrentImages } from './AppCurrentImages';
import { AppBrandKit } from './AppBrandKit';
import { AppManualSet } from './AppManualSet';

interface AppImagesPanelProps {
  appCfgId: number;
  /** Whose deliverable set this panel manages (roku, firetv). */
  platform: FactoryAssetPlatform;
  disabled?: boolean;
  /** The platform row's title: the TV Brand kit's default app name on the tiles. */
  appTitle?: string;
}

const noticeKey = (appCfgId: number) => `tvTilesNoticeDismissed:${appCfgId}`;
function readDismissed(appCfgId: number): boolean {
  try {
    return window.localStorage.getItem(noticeKey(appCfgId)) === '1';
  } catch {
    return false;
  }
}

/**
 * The Images surface orchestrator — platform-generic since 3.34.0 (the Fire TV
 * tab mounts it for the set an Android build is made with; Roku keeps its own).
 * Every card on this panel reads the factory gateway, so an unreachable gateway
 * must say so — three cards each quietly returning null would leave the whole
 * panel blank (the pre-rework tab always had non-factory content to fall back
 * on; this one does not).
 */
export function AppImagesPanel({ appCfgId, platform, disabled, appTitle }: AppImagesPanelProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const [dismissed, setDismissed] = useState(() => readDismissed(appCfgId));
  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(noticeKey(appCfgId), '1');
    } catch {
      /* private mode: the notice stays dismissed for this visit only */
    }
  };

  if (assetSetsQuery.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        Image management is temporarily unavailable. Your app and its current images are
        unaffected — try again in a few minutes, or contact support if this persists.
      </Alert>
    );
  }
  if (assetSetsQuery.isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={120} radius="md" />
        <Skeleton height={220} radius="md" />
      </Stack>
    );
  }

  // TV graphics v2 (design D6): a live set from before composer v2 gets one
  // dismissible nudge — regenerating never changes anything until approved.
  const showTvNotice =
    (platform === 'firetv' || platform === 'roku') &&
    !dismissed &&
    BrandKitUtils.needsTvTilesNotice(assetSetsQuery.data?.assetSets ?? [], assetSetsQuery.data?.currentImages ?? [], platform);

  return (
    <Stack gap="lg">
      {showTvNotice && (
        <Alert icon={<IconSparkles size={16} />} color="blue" withCloseButton onClose={dismiss} data-testid="tv-tiles-notice">
          {TV_BRAND_KIT_COPY.v1Notice}
        </Alert>
      )}
      <AppCurrentImages appCfgId={appCfgId} platform={platform} />
      <AppBrandKit appCfgId={appCfgId} platform={platform} disabled={disabled} appTitle={appTitle} />
      <AppManualSet appCfgId={appCfgId} platform={platform} disabled={disabled} />
    </Stack>
  );
}
